import { FC, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Upload, Button, message } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  UpOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { observer } from 'mobx-react';
import UnitItem from '@/components/UnitItem';
import { useBackground, useBgImg, useProduct } from '@/store';
import { $fm, $t } from '@/Intl';
import { useUpdateEffect } from '@/utils/hooks';
import { bgList } from './BgImgs';
import './BgSet.less';

type Bg = {
  src: string;
  name: string;
};

// 型号间的差异特性&数据
const useBGProps = (): {
  direction: 'row' | 'column'; // 水平布局 | 垂直布局
  defaultBgList: Bg[]; // 各型号的壁纸列表路径
} => {
  const [product] = useProduct();

  const defaultBgList: Bg[] = bgList[product].map((src) => {
    return {
      src,
      name: '',
    };
  });

  switch (product) {
    case 'gxv3470':
      return {
        direction: 'row',
        defaultBgList,
      };
    default:
      return {
        direction: 'column',
        defaultBgList,
      };
  }
};

// 列表选择组件
const BgSelect: FC<{
  list: Bg[];
  active: number;
  onSelect: (index: number) => void;
}> = (props) => {
  const { list, onSelect, active } = props;
  const { direction } = useBGProps();
  const [index, setIndex] = useState(0);
  const isClicking = useRef(false);
  const len = list.length;
  const handlePrev = useCallback(() => {
    if (index > 0) {
      setIndex(index - 1);
    }
  }, [index]);

  const handleNext = useCallback(() => {
    if (index + 4 < len) {
      setIndex(index + 1);
    }
  }, [index, len]);

  useEffect(() => {
    let _index = 0;
    _index = active + 4 > len ? len - 4 : active;
    if (isClicking.current) {
      isClicking.current = false;
      return;
    }
    setIndex(_index);
  }, [active, len]);

  return (
    <div className={`bg-select-${direction}`}>
      <div className='bg-prev' onClick={handlePrev}>
        {direction === 'column' ? <LeftOutlined /> : <UpOutlined />}
      </div>
      {list.slice(index, index + 4).map((item, i) => {
        return (
          <div
            className={`bg-img-item ${active === index + i ? 'active' : ''}`}
            key={item.src}
            onClick={() => {
              isClicking.current = true;
              onSelect(index + i);
            }}
          >
            <img alt='bg' src={item.src} />
          </div>
        );
      })}
      <div className='bg-next' onClick={handleNext}>
        {direction === 'column' ? <RightOutlined /> : <DownOutlined />}
      </div>
    </div>
  );
};

export const useBgList = (): Bg[] => {
  const [bgImg] = useBgImg();
  const { defaultBgList } = useBGProps();

  const bgList = useMemo(() => {
    return bgImg
      ? [
          ...defaultBgList,
          {
            src: URL.createObjectURL(bgImg),
            name: bgImg.name,
          },
        ]
      : defaultBgList;
  }, [bgImg, defaultBgList]);

  return bgList;
};

export const useWallpaper = () => {
  const list = useBgList();
  const [{ wallpaper = 0 }] = useBackground();

  return list[wallpaper].src;
};

const BgSet: FC = (props) => {
  const [{ wallpaper }, updateBackground] = useBackground();
  // 根据不同的布局，设置不同的css样式
  const { direction, defaultBgList } = useBGProps();
  const [bgImg, setBgImg] = useBgImg();
  const bgList = useBgList();
  // 选中后赋值
  const handleSelect = useCallback(
    (i: number) => {
      updateBackground({ wallpaper: i });
    },
    [updateBackground],
  );

  // 上传拦截
  const handleBeforeUpload = (file: File) => {
    if (!/\.(jpg|svg|png|jpeg|bmp)$/.test(file.name)) {
      message.error($t('IMG_ERROR'));
      return false;
    }
    setBgImg(file);
    return false;
  };

  useUpdateEffect(() => {
    if (bgImg) {
      handleSelect(defaultBgList.length);
    } else {
      handleSelect(0);
    }
  }, [bgImg, handleSelect]);

  return (
    <div className='bg-set'>
      <div className={`bg-preview bg-preview-${direction}`}>
        <img alt='bg' src={bgList[wallpaper!]?.['src']} />
      </div>
      <BgSelect list={bgList} onSelect={handleSelect} active={wallpaper!} />

      <UnitItem label={$fm('ADD_BACKGROUND')} className='bg-upload'>
        <Upload
          showUploadList={false}
          beforeUpload={handleBeforeUpload}
          accept='.png,.svg,.jpg,.jpeg,.bmp'
        >
          <Button type='primary'>{$fm('UPLOAD')}</Button>
        </Upload>
      </UnitItem>
    </div>
  );
};

export default observer(BgSet);
